import { Animated, Easing } from 'react-native';

import { isFunction as _isFunction, isUndefined as _isUndefined, map as _map } from 'lodash';
import memoize from 'memoize-one';

import { isIPhoneDeviceWithNotch } from '@vfgroup-oneplatform/foundation/Utils/Platform';

import { Tray_Actions_Types } from '../Utils';

const isIPhoneWithNotch = isIPhoneDeviceWithNotch();

const SUB_TRAY_ANIMATION_DURATION = Object.freeze({
  [Tray_Actions_Types.OPEN]: 333, // 200 orignally
  [Tray_Actions_Types.SWITCH]: 200,
  [Tray_Actions_Types.CHANGE]: 100,
  [Tray_Actions_Types.CLOSE]: 150,
});

class AnimationManager {
  constructor(config = {}) {
    this.__animations = Object.freeze({
      modalAnimation: new Animated.Value(0),
      subTrayAnimation: new Animated.Value(0),
      tabAnimations: [],
      trayAnimation: new Animated.Value(1),
    });

    this.subTrayTransitionTimeOut = undefined;

    this.__callbacks = {
      modalAnimationFinished: () => { },
    };
  }

  _animateModal = isVisible => {
    const modalAnimateToValue = isVisible ? 1 : 0;

    Animated.timing(this.__animations.modalAnimation, {
      toValue: modalAnimateToValue,
      duration: isVisible ? 333 : 150,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (_isFunction(this.__callbacks.modalAnimationFinished)) {
        this.__callbacks.modalAnimationFinished(isVisible, finished);
      }
    });
  };

  _animateSubTray = (isVisible, trayAction) => {
    const subTrayAnimateToValue = isVisible ? 1 : 0;

    if (trayAction === Tray_Actions_Types.SWITCH) {
      clearTimeout(this.subTrayTransitionTimeOut);

      this.subTrayTransitionTimeOut = setTimeout(() => {
        this._prepareSubTrayAnimation(
          SUB_TRAY_ANIMATION_DURATION[trayAction],
          subTrayAnimateToValue,
          100,
        ).start();
      }, SUB_TRAY_ANIMATION_DURATION[Tray_Actions_Types.CHANGE]);
    } else if (trayAction === Tray_Actions_Types.CHANGE) {
      this._prepareSubTrayAnimation(SUB_TRAY_ANIMATION_DURATION[trayAction], 0).start();
    } else {
      clearTimeout(this.subTrayTransitionTimeOut);

      this._prepareSubTrayAnimation(
        SUB_TRAY_ANIMATION_DURATION[trayAction],
        subTrayAnimateToValue,
      ).start();
    }
  };

  _animateTabs = activeIndex => {
    const animations = _map(this.__animations.tabAnimations, (tabAnimation, index) => {
      const tabAnimateToValue = activeIndex === index ? 1 : 0;

      return Animated.timing(tabAnimation, {
        toValue: tabAnimateToValue,
        duration: 100,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: false,
      });
    });

    Animated.parallel(animations).start();
  };

  _animateTray = (upTray, dropTray) => {
    if (upTray) {
      this.__animations.trayAnimation.setValue(0);
    } else if (dropTray) {
      this.__animations.trayAnimation.setValue(1);
    }

    const trayAnimateToValue = dropTray ? 0 : 1;
    const duration = dropTray ? 600 : 200;

    // setTimeout was i the original animation, i believe to solve so sort of issue so i kept it
    setTimeout(() => {
      Animated.timing(this.__animations.trayAnimation, {
        toValue: trayAnimateToValue,
        duration,
        delay: 0,
        useNativeDriver: false,
      }).start();
    }, 0);
  };

  animateModal = memoize(this._animateModal);

  animateSubTray = memoize(this._animateSubTray);

  animateTabs = memoize(this._animateTabs);

  animateTray = memoize(this._animateTray);

  _prepareSubTrayAnimation = (duration, toValue, delay) => {
    return Animated.timing(this.__animations.subTrayAnimation, {
      toValue,
      duration,
      delay,
      easing: Easing.inOut(Easing.linear),
      useNativeDriver: true,
    });
  };

  _prepareTabAnimatedValues = numberOfTabs => {
    if (!_isUndefined(numberOfTabs) && numberOfTabs !== this.__animations.tabAnimations.length) {
      for (let i = 0; i < numberOfTabs; i++) {
        this.__animations.tabAnimations[i] = new Animated.Value(0);
      }
    }
  };

  _getModalInterpolations = modalInfo => {
    const lottieViewHeight = isIPhoneWithNotch
      ? (modalInfo.lottieViewHeight || 0) + 20
      : modalInfo.lottieViewHeight || 0;
    const inputRange = isIPhoneWithNotch ? [0, 0.1999999999, 0.2, 1] : [0, 0.1499999999, 0.15, 1];

    return {
      transform: [
        {
          translateY: this.__animations.modalAnimation.interpolate({
            inputRange: [0, 1],
            outputRange: [lottieViewHeight, 0],
          }),
        },
      ],
      opacity: this.__animations.modalAnimation.interpolate({
        inputRange: inputRange,
        outputRange: [0, 0, 1, 1],
      }),
    };
  };

  _getOverlayInterpolations = () => {
    return {
      opacity: this.__animations.modalAnimation.interpolate({
        inputRange: [0, 0.6, 1],
        outputRange: [0, 1, 1],
      }),
    };
  };

  _getCollapseIconInterpolations = collapseIconInfo => {
    const { lottieViewHeight = 0 } = collapseIconInfo || {};
    //75 is the height of the visible part of the tray when it’s closed (the lottie part)
    //7 is required to make the collapseIcon closer to the tray as the design
    const height = -lottieViewHeight + 75 + 7;

    return {
      transform: [
        {
          translateY: this.__animations.modalAnimation.interpolate({
            inputRange: [0, 0.6, 1],
            outputRange: [0, height, height],
          }),
        },
      ],
      opacity: this.__animations.modalAnimation.interpolate({
        inputRange: [0, 0.42, 0.6, 1],
        outputRange: [0, 0, 1, 1],
      }),
    };
  };

  _getTrayHeightAnimationInterpolations = trayHeightInfo => {
    const { lottieViewHeight = 0 } = trayHeightInfo || {};
    return {
      transform: [
        {
          translateY: this.__animations.modalAnimation.interpolate({
            inputRange: [0, 1],
            outputRange: [
              //75 is the height of the visible part of the tray when it's closed (the lottie part)
              lottieViewHeight - 75 - (isIPhoneWithNotch ? 29 : 12),
              0,
            ],
          }),
        },
      ],
    };
  };

  _getSubTrayDescriptionInterpolations = trayAction => {
    const inputRange = [0, 1];

    const opacityOutputRange = [0, 1];
    let translateYOutputRange = [20, 0];

    if (trayAction === Tray_Actions_Types.CHANGE) {
      translateYOutputRange = [20, 0];
    }

    if (trayAction === Tray_Actions_Types.CLOSE) {
      translateYOutputRange = [0, 0];
    }

    return {
      transform: [
        {
          translateY: this.__animations.subTrayAnimation.interpolate({
            inputRange,
            outputRange: translateYOutputRange,
          }),
        },
      ],
      opacity: this.__animations.subTrayAnimation.interpolate({
        inputRange,
        outputRange: opacityOutputRange,
      }),
    };
  };

  _getSubTrayCardsInterpolations = trayAction => {
    return this._getSubTrayDescriptionInterpolations(trayAction);
  };

  _getSubTrayFooterInterpolations = trayAction => {
    return this._getSubTrayDescriptionInterpolations(trayAction);
  };

  _getSubTrayHeaderInterpolations = trayAction => {
    return this._getSubTrayDescriptionInterpolations(trayAction);
  };

  _getTabsInterpolations = (colors = {}) => {
    return _map(this.__animations.tabAnimations, tabAnimation => ({
      title: {
        color: tabAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [colors.inactiveTrayItemColor, colors.activeTabColorTwo],
        }),
      },
      icon: {
        tintColor: tabAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [colors.inactiveTrayItemColor, colors.activeTabColorTwo],
        }),
      },
    }));
  };

  _getTrayLottieViewInterpolations = () => {
    //transform animation does not work properly with TrayHOC
    return {
      bottom: this.__animations.trayAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [-300, 0],
      }),
    };
  };

  _getTrayBottomBarInterpolations = () => {
    return {
      transform: [
        {
          translateY: this.__animations.trayAnimation.interpolate({
            inputRange: [0, 1],
            outputRange: [300, 0],
          }),
        },
      ],
    };
  };

  getModalAnimations = (isVisible, options = {}) => {
    const { onAnimationFinish } = options;

    this.__callbacks.modalAnimationFinished = onAnimationFinish;

    this.animateModal(isVisible);

    const animations = {
      ...this.__animations,
      modalAnimation: this._getModalInterpolations(options),
      overlayAnimation: this._getOverlayInterpolations(),
      collapseIconAnimation: this._getCollapseIconInterpolations(options),
      heightAnimation: this._getTrayHeightAnimationInterpolations(options),
    };

    return animations;
  };

  getSubTrayAnimations = (isVisible, trayAction) => {
    this.animateSubTray(isVisible, trayAction);

    const animations = {
      descriptionAnimation: this._getSubTrayDescriptionInterpolations(trayAction),
      cardsAnimation: this._getSubTrayCardsInterpolations(trayAction),
      footerAnimation: this._getSubTrayFooterInterpolations(trayAction),
      headerAnimation: this._getSubTrayHeaderInterpolations(trayAction),
    };

    return animations;
  };

  getTabAnimations = (activeIndex, options = {}) => {
    const { numberOfTabs, colors } = options || {};

    this._prepareTabAnimatedValues(numberOfTabs);

    this.animateTabs(activeIndex);

    const animations = {
      tabsAnimation: this._getTabsInterpolations(colors),
    };

    return animations;
  };

  getTrayAnimations = (upTray, dropTray) => {
    this.animateTray(upTray, dropTray);

    const animations = {
      lottieViewAnimation: this._getTrayLottieViewInterpolations(),
      bottomBarAnimation: this._getTrayBottomBarInterpolations(),
    };

    return animations;
  };
}

export default AnimationManager;
